/**
 * @typedef {import('../App/shared/AgentService/Agent').AgentPersonalData} Agent
 * @typedef {import('./FeaturedBlogsProps').default} Props
 */

import { BaseSVG } from '../shared/BaseSVG';
import BlogPostExcerptCard from '../shared/BlogPostExcerptCard';
import flags from '../shared/Flags';
import images from '../config/local/images';
import Loading from '../shared/Loading';
import React, { useEffect, useMemo, useState } from 'react';
import useGetAgentsPersonalData from '../Hooks/Agent/Getters/useGetAgentsPersonalData/useGetAgentsPersonalData';
import useGetBlogs from '../Hooks/Shared/Getters/useGetBlogs/useGetBlogs';
import useIsGated from '../Hooks/Shared/useIsGated/useIsGated';
import useTheme from '../Hooks/Shared/useTheme/useTheme';

/**
 * @param {Props} props
 * @returns	{JSX.Element | null}
 */
function FeaturedBlogs(props) {
	const role = props.userRole;
	const theme = useTheme();
	const [agentInfo, setAgentInfo] = useState(getHomeownerAgent());
	const isAgent = role === 'agent';
	const agent = useGetAgentsPersonalData(!isAgent);
	const blogs = useGetBlogs();
	const gated = useIsGated(flags.agent.blogWidget, { role });
	const isGettingData = useMemo(
		() => agent.isGettingData || blogs.isGettingData || gated.isGettingData,
		[agent, blogs, gated]
	);

	useEffect(() => {
		if (gated.isGated || isGettingData) {
			return;
		}

		if (isAgent) {
			setAgentInfo(getAgentInfo());
			return;
		}

		setAgentInfo(getHomeownerAgent());
	}, [agent, gated]);

	function getAgentInfo() {
		const { email, headshot, name } = agent.data ?? {};
		return {
			email: email ?? '',
			headshotUrl: headshot ? `/${headshot}` : '',
			name: `${name?.first} ${name?.last}`.trim(),
			slug: agent.data?.slug ?? '',
		};
	}

	function getHomeownerAgent() {
		return {
			email: theme.agentEmail ?? '',
			headshotUrl: theme.agentHeadshotUrl ?? '',
			name: theme.agentName ?? '',
			slug: theme.agentSlug ?? '',
		};
	}

	if (isGettingData) {
		return (
			<React.Fragment>
				<header className="h-ms-4 mb-4">{props.title}</header>
				<Loading />
			</React.Fragment>
		);
	}

	if (!blogs?.data?.length || gated.isGated) {
		return null;
	}

	return (
		<section className="mt-10">
			<header className="h-ms-4 mb-4">{props.title}</header>
			{!!props.showFeatureImage && <BaseSVG path={images.blog.header} />}

			<section className="listing listing-col-size-3">
				{blogs?.data?.map((postExcerpt) => (
					<BlogPostExcerptCard
						agentEmail={agentInfo.email || postExcerpt.author.email}
						agentSlug={agentInfo.slug || postExcerpt.author.slug}
						authorName={agentInfo.name || postExcerpt.author.name}
						content={postExcerpt.content}
						date={postExcerpt.date}
						headshotUrl={
							agentInfo.headshotUrl ||
							postExcerpt.author.headshotUrl
						}
						imageUrl={postExcerpt.imageUrl}
						key={postExcerpt.title}
						slug={postExcerpt.slug}
						title={postExcerpt.title}
					/>
				))}
			</section>
		</section>
	);
}

export default React.memo(FeaturedBlogs);
